import { Body, Tooltip } from '@assembly-js/design-system';
import React from 'react';

import { ACH_CAPABILITY_DISABLED_TOOLTIP } from 'src/features/payments/hooks/useHasACHCapability';
import { useOpenStripeAchSetup } from 'src/features/payments/hooks/useOpenStripeAchSetup';
import { WebLink } from 'src/theme/colors';

/** Fixed chrome for every ACH-gated surface — do not re-specify at call sites. */
export const ACH_DISABLED_TOOLTIP_CLASSNAME = 'w-[400px] whitespace-normal';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

/**
 * Tooltip body shown when bank/ACH is disabled because the connected account
 * can't accept ACH yet. Includes an inline link that opens Stripe's onboarding
 * flow so the internal user can complete the ACH Direct Debit requirements. The
 * link is a button (not the design-system TextLink, which is display:flex) so
 * it flows inline with the sentence.
 */
export const ACHDisabledTooltipContent: React.FC = () => {
  const { openStripeAchSetup, canOpenStripeAchSetup } = useOpenStripeAchSetup();

  return (
    <Body size="sm">
      {ACH_CAPABILITY_DISABLED_TOOLTIP}{' '}
      {canOpenStripeAchSetup && (
        <button
          type="button"
          onClick={() => void openStripeAchSetup()}
          className="m-0 cursor-pointer border-0 bg-transparent p-0 underline"
          style={{ color: WebLink, font: 'inherit' }}
        >
          Set up ACH in Stripe
        </button>
      )}
    </Body>
  );
};

type ACHDisabledTooltipProps = {
  children: React.ReactNode;
  /** Layout only — content and width are fixed. Default bottom. */
  position?: TooltipPosition;
  disabled?: boolean;
};

/**
 * The one ACH-disabled tooltip. Every gated surface should use this (or
 * {@link getACHDisabledTooltipHostProps} when a host already owns Tooltip).
 */
export const ACHDisabledTooltip: React.FC<ACHDisabledTooltipProps> = ({
  children,
  position = 'bottom',
  disabled = false,
}) => (
  <Tooltip
    content={<ACHDisabledTooltipContent />}
    position={position}
    tooltipClassname={ACH_DISABLED_TOOLTIP_CLASSNAME}
    disabled={disabled}
  >
    {children}
  </Tooltip>
);

/**
 * Props for hosts that already wrap a Tooltip (e.g. SettingsToggleCard).
 * Keeps content + width identical to {@link ACHDisabledTooltip}.
 */
export const getACHDisabledTooltipHostProps = (
  position: TooltipPosition = 'right',
) => ({
  tooltipContent: <ACHDisabledTooltipContent />,
  tooltipClassname: ACH_DISABLED_TOOLTIP_CLASSNAME,
  tooltipPosition: position,
});
